import React, { useEffect, useState } from "react";
import {
  Modal,
  Button,
  Table,
  Segment,
  Header,
  Message,
  Form,
  Loader,
  Dimmer,
  Label,
} from "semantic-ui-react";
import axios from "../../../apis/dvdlocator";

interface ExistingListing {
  asin: string;
  msku: string | null;
  listing_title: string | null;
}

interface AddListingToProductProps {
  productId: number;
  productTitle: string;
  onClose: (didCreate?: boolean) => void;
}

export function AddListingToProduct({
  productId,
  productTitle,
  onClose,
}: AddListingToProductProps) {
  const [existingListings, setExistingListings] = useState<ExistingListing[]>([]);
  const [loadingListings, setLoadingListings] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [asin, setAsin] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const abortController = new AbortController();

    async function fetchListings() {
      setLoadingListings(true);
      setLoadError(null);
      try {
        const res = await axios.get(`/product/${productId}/listings`, {
          signal: abortController.signal,
        });
        setExistingListings(res.data.listings ?? []);
      } catch (err: any) {
        if (!abortController.signal.aborted) {
          setLoadError(
            err.response?.data?.message ||
              "Failed to load existing listings. Please close and try again."
          );
        }
      } finally {
        if (!abortController.signal.aborted) setLoadingListings(false);
      }
    }

    fetchListings();
    return () => abortController.abort();
  }, [productId]);

  const asinTrimmed = asin.trim().toUpperCase();
  const isValidFormat = /^[A-Z0-9]{10}$/.test(asinTrimmed);
  const isDuplicate = existingListings.some(
    (l) => l.asin.toUpperCase() === asinTrimmed
  );
  const canSubmit = isValidFormat && !isDuplicate && !isSubmitting;

  const handleSubmit = async () => {
    setSubmitted(true);
    if (!canSubmit) return;

    setIsSubmitting(true);
    setSubmitError(null);
    try {
      await axios.post(`/product/${productId}/listing`, { asin: asinTrimmed });
      setSuccess(true);
    } catch (err: any) {
      if (err.response?.status === 409) {
        setSubmitError("This ASIN is already linked to this product.");
      } else if (err.response?.status === 404) {
        setSubmitError(
          "This ASIN does not exist in the system. Listings can only be linked if they have already been synced from Amazon."
        );
      } else {
        setSubmitError(
          err.response?.data?.message || "An error occurred. Please try again."
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal open onClose={() => onClose()} closeOnDimmerClick={false} size="large">
      <Modal.Header>Add New Listing to Product</Modal.Header>
      <Modal.Content>
        {/* Selected Product */}
        <Header as="h4" dividing>
          Selected Product
        </Header>
        <Table definition compact>
          <Table.Body>
            <Table.Row>
              <Table.Cell width={4}>Product ID</Table.Cell>
              <Table.Cell>{productId}</Table.Cell>
            </Table.Row>
            <Table.Row>
              <Table.Cell>Product Title</Table.Cell>
              <Table.Cell>{productTitle}</Table.Cell>
            </Table.Row>
          </Table.Body>
        </Table>

        {/* Existing listings */}
        <Header as="h4" dividing style={{ marginTop: "1.5em" }}>
          Existing Listings for This Product
        </Header>
        <p style={{ color: "#666", fontSize: "0.9em" }}>
          Review existing listings before adding a new ASIN.
        </p>

        {loadError && (
          <Message negative>
            <p>{loadError}</p>
          </Message>
        )}

        {loadingListings ? (
          <Segment style={{ minHeight: "60px" }}>
            <Dimmer active inverted>
              <Loader size="small">Loading listings…</Loader>
            </Dimmer>
          </Segment>
        ) : (
          <Table celled compact>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>ASIN</Table.HeaderCell>
                <Table.HeaderCell>MSKU</Table.HeaderCell>
                <Table.HeaderCell>Listing Title</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {existingListings.length === 0 ? (
                <Table.Row>
                  <Table.Cell colSpan={2} textAlign="center" style={{ color: "#999" }}>
                    No listings yet
                  </Table.Cell>
                </Table.Row>
              ) : (
                existingListings.map((listing) => (
                  <Table.Row key={listing.asin}>
                    <Table.Cell>{listing.asin}</Table.Cell>
                    <Table.Cell>{listing.msku ?? "—"}</Table.Cell>
                    <Table.Cell>{listing.listing_title ?? "—"}</Table.Cell>
                  </Table.Row>
                ))
              )}
            </Table.Body>
          </Table>
        )}

        {/* Add new listing */}
        {!success && (
          <>
            <Header as="h4" dividing style={{ marginTop: "1.5em" }}>
              Link Listing to Product
            </Header>
            <p style={{ color: "#666", fontSize: "0.9em" }}>
              Enter an ASIN to link an existing listing to this product.
            </p>

            {submitError && (
              <Message negative>
                <p>{submitError}</p>
              </Message>
            )}

            <Form>
              <Form.Field>
                <label>New ASIN</label>
                <Form.Input
                  placeholder="Enter ASIN"
                  value={asin}
                  onChange={(e) => {
                    setAsin(e.target.value);
                    setSubmitError(null);
                  }}
                  maxLength={10}
                  disabled={isSubmitting}
                />
                {submitted && !asinTrimmed && (
                  <Label basic color="red" pointing>
                    ASIN is required
                  </Label>
                )}
                {submitted && asinTrimmed && !isValidFormat && (
                  <Label basic color="red" pointing>
                    ASIN must be exactly 10 alphanumeric characters
                  </Label>
                )}
                {asinTrimmed && isValidFormat && isDuplicate && (
                  <Label basic color="red" pointing>
                    This ASIN is already linked to this product.
                  </Label>
                )}
              </Form.Field>
            </Form>
          </>
        )}

        {success && (
          <Message positive>
            <Message.Header>Listing Linked Successfully!</Message.Header>
            <p>
              ASIN <strong>{asinTrimmed}</strong> has been linked to this product.
            </p>
          </Message>
        )}
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={() => onClose(success)}>
          {success ? "Close" : "Cancel"}
        </Button>
        {!success && (
          <Button
            primary
            onClick={handleSubmit}
            disabled={!canSubmit || loadingListings}
            loading={isSubmitting}
          >
            Add Listing
          </Button>
        )}
      </Modal.Actions>
    </Modal>
  );
}
